<template>
  <div>
    <div class="flex gap-4">
      <template v-if="mode === 'view'">
        <div>
          <ViewModeLabel>SMS Template</ViewModeLabel>
          <ViewModeValue>{{ selectedSmsOption }}</ViewModeValue>
        </div>
      </template>
      <template v-else>
        <FormKit
          v-model="selectedSmsOption"
          label="SMS Template"
          name="sms_template"
          type="select"
          :options="smsTemplates.map(x => x.name)"
          validation="required"
          outer-class="!m-0 width-md"
        />
      </template>
      <UserSelector
        v-model:selected-type="recipientType"
        v-model:selected-user-ids="recipientUserIds"
        label="Recipient"
        name="recipient_type"
        :type-options="recipientTypeOptions"
        show-users-for-type="account_users"
        :mode="mode"
        show-phone-warning
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { ActionableProps } from '../types';
import type {
  WorkflowActionRecipientType,
  WorkflowActionableSmsParams,
  WorkflowActionSmsEntity,
} from '@/api/entity_types/workflow/WorkflowAction';
import { Api } from '@/api';
import type { SmsTemplateEntity } from '@/api/entity_types/SmsTemplate';
import ViewModeLabel from '../../common/ViewModeLabel.vue';
import ViewModeValue from '../../common/ViewModeValue.vue';
import UserSelector from './common/UserSelector.vue';

const { actionable: smsActionable, mode } = defineProps<ActionableProps<WorkflowActionSmsEntity>>();

const smsTemplates = ref<SmsTemplateEntity[]>([]);
const selectedSmsOption = ref<string>('');
const recipientType = ref<WorkflowActionRecipientType>('candidate');
const recipientUserIds = ref<number[]>([]);

const recipientTypeOptions = [
  { label: 'Candidate', value: 'candidate' },
  { label: 'Account Users', value: 'account_users' },
];

const initSelectedOption = (smsActionable?: WorkflowActionSmsEntity) => {
  const matchingSmsTemplate = smsTemplates.value.find(x => x.id === smsActionable?.sms_template_id);
  if (matchingSmsTemplate) selectedSmsOption.value = matchingSmsTemplate.name;
  else selectedSmsOption.value = smsTemplates.value[0]?.name || '';

  recipientType.value = smsActionable?.recipient_type || 'candidate';
  recipientUserIds.value = smsActionable?.recipient_user_ids || [];
};

const buildActionableParams = () => {
  const selectedSmsTemplate = smsTemplates.value.find(x => x.name === selectedSmsOption.value);
  if (!selectedSmsTemplate) return;
  const params: WorkflowActionableSmsParams = {
    actionable_type: 'WorkflowActionSms',
    actionable: {
      sms_template_id: selectedSmsTemplate.id,
      recipient_type: recipientType.value,
      recipient_user_ids: recipientUserIds.value,
    },
  };
  return params;
};

const resetFields = (smsActionable: WorkflowActionSmsEntity) => {
  initSelectedOption(smsActionable);
};

defineExpose({
  buildActionableParams,
  resetFields,
});

onMounted(async () => {
  const response = await Api.SmsTemplates.listSmsTemplates();
  smsTemplates.value = response.records;
  initSelectedOption(smsActionable);
});
</script>

<style scoped>
.width-md {
  @apply w-[18rem] max-w-[18rem];
}
</style>
